/**
 * EcommerceVoiceStrip
 *
 * Fixed bottom-strip voice UI for desktop sessions with flavor.callView === 'minimized'.
 * (Historically ecommerce-only; now any flavored widget may opt in.) Requires
 * window.innerWidth > 768.
 *
 * Appended to document.body (outside shadow DOM). Control actions delegate to
 * VoiceInterface via callbacks; optional getVoiceUiState() keeps icons in sync.
 *
 * Public API:
 *   strip.show(opts?)     – opts.reset false = reveal without clearing transcript
 *   strip.hide()
 *   strip.syncFromVoice() – refresh mute / pause / speaker icons from getVoiceUiState
 *   strip.updateTranscript(text) – assistant line (replaces any user text on the strip)
 *   strip.updateUserTranscript(text, isFinal) – user STT on the same line (replaces agent text); no "You:" prefix; warm text color
 *   strip.updateTimer(text)
 *   strip.setMuted(bool)  – legacy sync (prefer syncFromVoice)
 *   strip.setHeld(bool)   – legacy pause visual (maps to paused)
 *   strip.updateRecipes(recipes) – refresh saved-recipes badge + popover
 *   strip.pulseRecipeBadge() – celebrate animation after recipe modal closes
 *   strip.destroy()
 */
import { configPrefersRtlText, dirForTranscriptSnippet } from '../widget/voice/rtlText.js';

export class EcommerceVoiceStrip {
  /**
   * @param {Object} opts
   * @param {Object} [opts.theme] – match floating pill: pillGradient, pillTextColor, pillDotColor, endCallButtonColor
   * @param {() => { isMicMuted: boolean, isPaused: boolean, isSpeakerMuted: boolean }} [opts.getVoiceUiState]
   * @param {Function} opts.onHangup
   * @param {Function} opts.onToggleMute
   * @param {Function} opts.onTogglePause
   * @param {Function} opts.onToggleSpeaker
   * @param {Function} opts.onSendText
   * @param {string} [opts.language] – Widget language (BCP-47); with `opts.direction` drives RTL transcript/input.
   * @param {string} [opts.direction] – Widget `direction` (`ltr` | `rtl`); Hebrew/Arabic `language` also enables RTL text.
   * @param {{ brandUrl: string, brandName: string } | null} [opts.footer] – "Powered by" row at bottom of strip
   */
  constructor(opts = {}) {
    this.theme = opts.theme || {};
    this.footer = opts.footer || null;
    this.getVoiceUiState = typeof opts.getVoiceUiState === 'function' ? opts.getVoiceUiState : null;
    this._language = typeof opts.language === 'string' ? opts.language : '';
    this._direction = opts.direction === 'rtl' || opts.direction === 'ltr' ? opts.direction : '';

    this.onHangup = opts.onHangup || (() => {});
    this.onToggleMute = opts.onToggleMute || (() => {});
    this.onTogglePause = opts.onTogglePause || (() => {});
    this.onToggleSpeaker = opts.onToggleSpeaker || (() => {});
    this.onSendText = opts.onSendText || (() => {});
    this.onAbout = typeof opts.onAbout === 'function' ? opts.onAbout : null;
    this.onSettings = typeof opts.onSettings === 'function' ? opts.onSettings : null;
    this.onRecipeSelect = typeof opts.onRecipeSelect === 'function' ? opts.onRecipeSelect : null;

    this._kbdOpen = false;
    this._recipePopoverOpen = false;
    this._recipes = [];
    this._recipeUnsub = null;
    this._pulseTimer = null;
    this._el = null;
    this._build();
  }

  _applyStripTextDirection() {
    if (!this._el) return;
    const stripCfg = { language: this._language, direction: this._direction };
    const rtlChrome = configPrefersRtlText(stripCfg);
    const transcript = this._el.querySelector('#ttp-ecom-transcript');
    const center = this._el.querySelector('#ttp-ecom-center');
    const inputRow = this._el.querySelector('#ttp-ecom-input-row');
    const input = this._el.querySelector('#ttp-ecom-input');
    const preferredLang = this._language && this._language.trim();

    if (transcript) {
      transcript.setAttribute('dir', dirForTranscriptSnippet(transcript.textContent || '', stripCfg));
      if (rtlChrome && preferredLang) transcript.setAttribute('lang', preferredLang);
      else transcript.removeAttribute('lang');
    }
    if (center) {
      if (rtlChrome) {
        center.setAttribute('dir', 'rtl');
        if (preferredLang) center.setAttribute('lang', preferredLang);
        else center.removeAttribute('lang');
      } else {
        center.removeAttribute('dir');
        center.removeAttribute('lang');
      }
    }
    if (rtlChrome) {
      if (inputRow) {
        inputRow.setAttribute('dir', 'rtl');
        if (preferredLang) inputRow.setAttribute('lang', preferredLang);
        else inputRow.removeAttribute('lang');
      }
      if (input) {
        input.setAttribute('dir', 'rtl');
        if (preferredLang) input.setAttribute('lang', preferredLang);
        else input.removeAttribute('lang');
      }
    } else {
      if (inputRow) {
        inputRow.removeAttribute('dir');
        inputRow.removeAttribute('lang');
      }
      if (input) {
        input.setAttribute('dir', 'auto');
        input.removeAttribute('lang');
      }
    }
  }

  _build() {
    this._injectStyles();
    const wrapper = document.createElement('div');
    wrapper.id = 'ttp-ecom-strip-wrapper';
    wrapper.innerHTML = this._html();
    document.body.appendChild(wrapper);
    this._el = wrapper;
    const strip = this._el.querySelector('#ttp-ecom-strip');
    if (strip) this._applyTheme(strip);
    this._applyStripTextDirection();
    this._wireEvents();
    this._el.style.display = 'none';
  }

  _applyTheme(strip) {
    const t = this.theme;
    const g = t.pillGradient || 'linear-gradient(135deg, #581c87, #312e81, #1e1b4b)';
    const fg = t.pillTextColor || '#ffffff';
    const accent = t.pillDotColor || '#4ade80';
    const endC = t.endCallButtonColor || '#ef4444';
    strip.style.setProperty('--ecom-strip-bg', g);
    strip.style.setProperty('--ecom-fg', fg);
    strip.style.setProperty('--ecom-accent', accent);
    strip.style.setProperty('--ecom-end', endC);
  }

  _html() {
    return `
      <div id="ttp-ecom-strip" role="region" aria-label="Voice call">
        <div id="ttp-ecom-live-edge" aria-live="polite">
          <div id="ttp-ecom-live-row">
            <span id="ttp-ecom-dot" class="ttp-ecom-live-dot"></span>
            <span id="ttp-ecom-live-label">LIVE · 00:00</span>
          </div>
        </div>
        <div id="ttp-ecom-main-row">
          <div id="ttp-ecom-left-controls" class="ttp-ecom-controls">
            <button type="button" class="ttp-ecom-ctrl" id="ttp-ecom-btn-mic" title="Mute / Unmute" aria-pressed="false">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="ttp-ecom-mic-on">
                <path d="M12 1a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V4a3 3 0 0 1 3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              </svg>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" class="ttp-ecom-mic-off" style="display:none" aria-hidden="true">
                <path d="M12 1a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V4a3 3 0 0 1 3-3z" stroke="currentColor" stroke-width="1.8"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke="currentColor" stroke-width="1.8"/>
                <line x1="5" y1="5" x2="19" y2="19" stroke="#fca5a5" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </button>
            <button type="button" class="ttp-ecom-ctrl" id="ttp-ecom-btn-pause" title="Pause / Resume" aria-pressed="false">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="ttp-ecom-pause-ico">
                <rect x="6" y="4" width="4" height="16" rx="1"/>
                <rect x="14" y="4" width="4" height="16" rx="1"/>
              </svg>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none" class="ttp-ecom-play-ico" style="display:none" aria-hidden="true">
                <polygon points="6,4 20,12 6,20"/>
              </svg>
            </button>
            <button type="button" class="ttp-ecom-ctrl" id="ttp-ecom-btn-speaker" title="Speaker" aria-pressed="false">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="ttp-ecom-spk-on">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" class="ttp-ecom-spk-off" style="display:none" aria-hidden="true">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <line x1="5" y1="5" x2="19" y2="19" stroke="#fca5a5" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </button>
          </div>

          <div id="ttp-ecom-center">
            <div id="ttp-ecom-transcript" aria-live="polite">Connecting…</div>
          </div>

          <div id="ttp-ecom-right-cluster" class="ttp-ecom-controls">
            <div class="ttp-ecom-mini-wave" aria-hidden="true">
              <span class="ttp-ecom-wb"></span><span class="ttp-ecom-wb"></span><span class="ttp-ecom-wb"></span>
              <span class="ttp-ecom-wb"></span><span class="ttp-ecom-wb"></span>
            </div>
            <button type="button" class="ttp-ecom-ctrl" id="ttp-ecom-btn-kbd" title="Type a message" aria-pressed="false">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M6 12h.01M10 12h.01M14 12h.01M18 12h.01M8 16h8"/>
              </svg>
            </button>
            <button type="button" class="ttp-ecom-ctrl ttp-ecom-ctrl-end" id="ttp-ecom-btn-end" title="End call">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.99 12 19.79 19.79 0 0 1 1.97 3.4 2 2 0 0 1 3.95 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
            </button>
          </div>
        </div>

        <div id="ttp-ecom-input-row">
          <input type="text" id="ttp-ecom-input" placeholder="Type your message…" autocomplete="off" />
          <button type="button" id="ttp-ecom-send" title="Send" class="ttp-ecom-send">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
        ${this._footerHtml()}
      </div>
    `;
  }

  _footerHtml() {
    const brand = this.footer
      ? `<span>Powered by <a href="${this.footer.brandUrl}" target="_blank" rel="noopener noreferrer"><b>${this.footer.brandName}</b></a></span>`
      : '';
    return `
        <div id="ttp-ecom-footer" class="ttp-ecom-footer">
          ${brand}
          <div class="ttp-ecom-recipe-wrap" id="ttp-ecom-recipe-wrap" hidden>
            <button type="button" class="ttp-ecom-recipe-btn" id="ttp-ecom-btn-recipe"
              title="Saved recipes" aria-label="Saved recipes" aria-expanded="false" aria-haspopup="true">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M5.5 3V8"/>
                <path d="M7.5 3V8"/>
                <path d="M9.5 3V8"/>
                <path d="M7.5 8V21"/>
                <path d="M16.5 3V21"/>
                <path d="M16.5 3C19 3 20.5 5.2 20.5 7.5V10"/>
              </svg>
              <span class="ttp-ecom-recipe-count" id="ttp-ecom-recipe-count">0</span>
            </button>
            <div class="ttp-ecom-recipe-popover" id="ttp-ecom-recipe-popover" role="menu" hidden></div>
          </div>
          <button type="button" class="ttp-ecom-info-btn" id="ttp-ecom-btn-settings" title="Call settings" aria-label="Call settings">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
          <button type="button" class="ttp-ecom-info-btn" id="ttp-ecom-btn-info" title="About" aria-label="About">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9"/>
              <line x1="12" y1="11" x2="12" y2="16"/>
              <circle cx="12" cy="7.7" r="1.05" fill="currentColor" stroke="none"/>
            </svg>
          </button>
        </div>`;
  }

  _wireEvents() {
    const $ = (id) => this._el.querySelector('#' + id);

    $('ttp-ecom-btn-mic').addEventListener('click', () => {
      this.onToggleMute();
      this.syncFromVoice();
    });

    $('ttp-ecom-btn-pause').addEventListener('click', () => {
      this.onTogglePause();
      this.syncFromVoice();
    });

    $('ttp-ecom-btn-speaker').addEventListener('click', () => {
      this.onToggleSpeaker();
      this.syncFromVoice();
    });

    $('ttp-ecom-btn-kbd').addEventListener('click', () => {
      this._kbdOpen = !this._kbdOpen;
      $('ttp-ecom-btn-kbd').classList.toggle('ttp-ecom-ctrl-active', this._kbdOpen);
      $('ttp-ecom-input-row').classList.toggle('open', this._kbdOpen);
      if (this._kbdOpen) setTimeout(() => $('ttp-ecom-input').focus(), 50);
    });

    const infoBtn = $('ttp-ecom-btn-info');
    if (infoBtn) {
      infoBtn.addEventListener('click', () => {
        if (this.onAbout) this.onAbout();
      });
    }

    const settingsBtn = $('ttp-ecom-btn-settings');
    if (settingsBtn) {
      settingsBtn.addEventListener('click', () => {
        if (this.onSettings) this.onSettings();
      });
    }

    this._wireRecipeBadge();

    $('ttp-ecom-btn-end').addEventListener('click', () => this.onHangup());

    const sendFn = () => {
      const input = $('ttp-ecom-input');
      const text = input.value.trim();
      if (!text) return;
      this.onSendText(text);
      this.updateUserTranscript(text, true);
      input.value = '';
    };

    $('ttp-ecom-send').addEventListener('click', sendFn);
    $('ttp-ecom-input').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') sendFn();
    });
  }

  /**
   * Subscribe to a recipe store; hydrates badge on mount.
   * @param {{ subscribe: Function, getAll: Function }} store
   */
  bindRecipeStore(store) {
    if (this._recipeUnsub) {
      this._recipeUnsub();
      this._recipeUnsub = null;
    }
    if (!store) return;
    this.updateRecipes(store.getAll());
    this._recipeUnsub = store.subscribe((recipes) => this.updateRecipes(recipes));
  }

  /**
   * @param {Array<{ id: string, title: string }>} recipes
   */
  updateRecipes(recipes) {
    this._recipes = Array.isArray(recipes) ? recipes : [];
    if (!this._el) return;

    const wrap = this._el.querySelector('#ttp-ecom-recipe-wrap');
    const countEl = this._el.querySelector('#ttp-ecom-recipe-count');
    const btn = this._el.querySelector('#ttp-ecom-btn-recipe');
    if (!wrap || !countEl || !btn) return;

    const n = this._recipes.length;
    countEl.textContent = String(n);
    wrap.hidden = n === 0;

    if (this._recipePopoverOpen) this._renderRecipePopover();
  }

  /** Play a short celebrate animation to draw attention after saving a recipe. */
  pulseRecipeBadge() {
    if (!this._el || this._recipes.length === 0) return;
    const btn = this._el.querySelector('#ttp-ecom-btn-recipe');
    const countEl = this._el.querySelector('#ttp-ecom-recipe-count');
    if (!btn) return;

    if (this._pulseTimer) {
      clearTimeout(this._pulseTimer);
      this._pulseTimer = null;
    }

    btn.classList.remove('ttp-ecom-recipe-btn--celebrate');
    countEl?.classList.remove('ttp-ecom-recipe-count--pop');
    // Force reflow so re-adding the class retriggers the animation.
    void btn.offsetWidth;
    btn.classList.add('ttp-ecom-recipe-btn--celebrate');
    countEl?.classList.add('ttp-ecom-recipe-count--pop');

    const cleanup = () => {
      btn.classList.remove('ttp-ecom-recipe-btn--celebrate');
      countEl?.classList.remove('ttp-ecom-recipe-count--pop');
      btn.removeEventListener('animationend', cleanup);
    };
    btn.addEventListener('animationend', cleanup);
    this._pulseTimer = setTimeout(cleanup, 1600);
  }

  _wireRecipeBadge() {
    const btn = this._el?.querySelector('#ttp-ecom-btn-recipe');
    const popover = this._el?.querySelector('#ttp-ecom-recipe-popover');
    if (!btn || !popover) return;

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._recipePopoverOpen = !this._recipePopoverOpen;
      btn.setAttribute('aria-expanded', this._recipePopoverOpen ? 'true' : 'false');
      if (this._recipePopoverOpen) {
        this._renderRecipePopover();
        popover.hidden = false;
      } else {
        popover.hidden = true;
      }
    });

    this._recipeOutsideClick = (e) => {
      if (!this._recipePopoverOpen) return;
      if (btn.contains(e.target) || popover.contains(e.target)) return;
      this._recipePopoverOpen = false;
      btn.setAttribute('aria-expanded', 'false');
      popover.hidden = true;
    };
    document.addEventListener('click', this._recipeOutsideClick);
  }

  _renderRecipePopover() {
    const popover = this._el?.querySelector('#ttp-ecom-recipe-popover');
    if (!popover) return;

    const items = this._recipes.slice(0, 3);
    const rows = items.map((r) => `
      <button type="button" class="ttp-ecom-recipe-row" data-recipe-id="${this._escapeAttr(r.id)}" role="menuitem">
        <span class="ttp-ecom-recipe-row-title">${this._escapeHtml(r.title)}</span>
      </button>`).join('');

    const more = this._recipes.length > 3
      ? `<div class="ttp-ecom-recipe-more">${this._recipes.length - 3} more saved</div>`
      : '';

    popover.innerHTML = `
      <span class="ttp-ecom-recipe-popover-label">Saved recipes</span>
      ${rows}
      ${more}`;

    popover.querySelectorAll('.ttp-ecom-recipe-row').forEach((row) => {
      row.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = row.getAttribute('data-recipe-id');
        const recipe = this._recipes.find((r) => r.id === id);
        if (recipe && this.onRecipeSelect) this.onRecipeSelect(recipe);
        this._recipePopoverOpen = false;
        popover.hidden = true;
        this._el?.querySelector('#ttp-ecom-btn-recipe')?.setAttribute('aria-expanded', 'false');
      });
    });
  }

  _escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  _escapeAttr(str) {
    return this._escapeHtml(str).replace(/'/g, '&#39;');
  }

  show(opts = {}) {
    if (!this._el) return;
    if (opts.reset !== false) this._reset();
    this._el.style.display = '';
    this.syncFromVoice();
    // Now that the strip has real dimensions, clamp any restored drag position.
    if (this._dragEnabled) requestAnimationFrame(() => this.reclampDrag());
  }

  hide() {
    if (!this._el) return;
    this._el.style.display = 'none';
  }

  syncFromVoice() {
    if (!this._el || !this.getVoiceUiState) return;
    let s;
    try {
      s = this.getVoiceUiState();
    } catch (_) {
      return;
    }
    const micMuted = !!s.isMicMuted;
    const paused = !!s.isPaused;
    const spkMuted = !!s.isSpeakerMuted;

    const micBtn = this._el.querySelector('#ttp-ecom-btn-mic');
    const micOn = this._el.querySelector('.ttp-ecom-mic-on');
    const micOff = this._el.querySelector('.ttp-ecom-mic-off');
    if (micBtn && micOn && micOff) {
      micBtn.classList.toggle('ttp-ecom-ctrl-warn', micMuted);
      micBtn.setAttribute('aria-pressed', micMuted ? 'true' : 'false');
      micOn.style.display = micMuted ? 'none' : 'block';
      micOff.style.display = micMuted ? 'block' : 'none';
    }

    const pauseBtn = this._el.querySelector('#ttp-ecom-btn-pause');
    const pauseIco = this._el.querySelector('.ttp-ecom-pause-ico');
    const playIco = this._el.querySelector('.ttp-ecom-play-ico');
    if (pauseBtn && pauseIco && playIco) {
      pauseBtn.classList.toggle('ttp-ecom-ctrl-warn', paused);
      pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      pauseIco.style.display = paused ? 'none' : 'block';
      playIco.style.display = paused ? 'block' : 'none';
    }

    const spkBtn = this._el.querySelector('#ttp-ecom-btn-speaker');
    const spkOn = this._el.querySelector('.ttp-ecom-spk-on');
    const spkOff = this._el.querySelector('.ttp-ecom-spk-off');
    if (spkBtn && spkOn && spkOff) {
      spkBtn.classList.toggle('ttp-ecom-ctrl-warn', spkMuted);
      spkBtn.setAttribute('aria-pressed', spkMuted ? 'true' : 'false');
      spkOn.style.display = spkMuted ? 'none' : 'block';
      spkOff.style.display = spkMuted ? 'block' : 'none';
    }

    const dot = this._el.querySelector('#ttp-ecom-dot');
    if (dot) dot.classList.toggle('paused', paused);

    this._syncMiniWave(micMuted || paused || spkMuted);
  }

  _syncMiniWave(frozen) {
    this._el?.querySelectorAll('.ttp-ecom-mini-wave .ttp-ecom-wb').forEach((b) => {
      b.classList.toggle('paused', frozen);
    });
  }

  updateTranscript(text) {
    if (!this._el) return;
    const el = this._el.querySelector('#ttp-ecom-transcript');
    if (!el) return;
    const t = text != null ? String(text) : '';
    if (!t.trim()) return;
    const stripCfg = { language: this._language, direction: this._direction };
    el.classList.remove('ttp-ecom-transcript--user');
    el.textContent = t;
    el.style.color = '';
    el.setAttribute('dir', dirForTranscriptSnippet(t, stripCfg));
    if (configPrefersRtlText(stripCfg) && this._language?.trim()) el.setAttribute('lang', this._language.trim());
    else el.removeAttribute('lang');
  }

  /**
   * User STT (interim + final) on the same line as the assistant transcript; replaces agent text while user is speaking.
   * No "You:" prefix — color distinguishes user vs agent.
   * @param {string} text
   * @param {boolean} [isFinal]
   */
  updateUserTranscript(text, isFinal) {
    if (!this._el) return;
    const el = this._el.querySelector('#ttp-ecom-transcript');
    if (!el) return;
    const stripCfg = { language: this._language, direction: this._direction };
    const utter = text != null ? String(text) : '';
    const trimmed = utter.trim();
    if (!trimmed) {
      el.textContent = '';
      el.classList.remove('ttp-ecom-transcript--user');
      el.style.color = '';
      el.removeAttribute('dir');
      el.removeAttribute('lang');
      return;
    }
    el.classList.add('ttp-ecom-transcript--user');
    el.textContent = utter;
    el.style.color = '';
    el.setAttribute('dir', dirForTranscriptSnippet(utter, stripCfg));
    const preferredLang = this._language && this._language.trim();
    if (configPrefersRtlText(stripCfg) && preferredLang) el.setAttribute('lang', preferredLang);
    else el.removeAttribute('lang');
  }

  updateTimer(timerText) {
    if (!this._el) return;
    const el = this._el.querySelector('#ttp-ecom-live-label');
    if (el) el.textContent = 'LIVE · ' + timerText;
  }

  setMuted(muted) {
    if (!this.getVoiceUiState) return;
    this.syncFromVoice();
  }

  setHeld(held) {
    if (!this.getVoiceUiState) return;
    this.syncFromVoice();
  }

  /**
   * Make the strip draggable by its body. The wrapper is centered with
   * `translateX(-50%)`; a drag offset is layered on top of that. Control buttons
   * and the text input don't start a drag. Position is clamped to the viewport
   * (re-clamped on show/resize) and optionally persisted under `storageKey`.
   *
   * @param {Object} opts
   * @param {boolean} [opts.persist=true]
   * @param {string}  [opts.storageKey]
   */
  enableDrag(opts = {}) {
    if (!this._el || this._dragEnabled) return;
    const wrapper = this._el; // fixed, centered via translateX(-50%)
    const handle = wrapper.querySelector('#ttp-ecom-strip');
    if (!handle) return;
    this._dragEnabled = true;
    this._dragPersist = opts.persist !== false;
    this._dragKey = opts.storageKey || '';
    this._dragOffset = { x: 0, y: 0 };

    handle.style.setProperty('cursor', 'move');
    handle.style.setProperty('touch-action', 'none');

    const EXCLUDE = 'button, a, input, textarea, select, [contenteditable]';
    const THRESHOLD = 5;
    let startX = 0, startY = 0, baseX = 0, baseY = 0, baseRect = null;
    let dragging = false, moved = false;

    const onMove = (e) => {
      const dx = e.clientX - startX, dy = e.clientY - startY;
      if (!moved && Math.hypot(dx, dy) < THRESHOLD) return;
      moved = true;
      this._dragOffset = this._clampDragOffset(baseX + dx, baseY + dy, baseRect);
      this._applyDragOffset();
    };
    const onUp = (e) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      handle.style.setProperty('cursor', 'move');
      try { handle.releasePointerCapture(e.pointerId); } catch (err) { /* not captured */ }
      dragging = false;
      if (moved && this._dragPersist && this._dragKey) {
        try { window.localStorage.setItem(this._dragKey, JSON.stringify(this._dragOffset)); } catch (err) { /* unavailable */ }
      }
    };
    const onDown = (e) => {
      if (e.button != null && e.button !== 0) return; // primary / touch / pen only
      if (dragging) return;
      if (e.target && e.target.closest && e.target.closest(EXCLUDE)) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startY = e.clientY;
      baseX = this._dragOffset.x;
      baseY = this._dragOffset.y;
      baseRect = this._dragBaseRect();
      handle.style.setProperty('cursor', 'grabbing');
      try { handle.setPointerCapture(e.pointerId); } catch (err) { /* unsupported */ }
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    };
    handle.addEventListener('pointerdown', onDown);

    // Restore saved offset (unclamped here — the strip is hidden until show(),
    // where it gets clamped against real dimensions).
    if (this._dragPersist && this._dragKey) {
      try {
        const saved = JSON.parse(window.localStorage.getItem(this._dragKey) || 'null');
        if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') {
          this._dragOffset = { x: saved.x, y: saved.y };
          this._applyDragOffset();
        }
      } catch (err) { /* ignore corrupt storage */ }
    }
  }

  /**
   * Base (offset-0) viewport rect used for clamping. Measures the *visible* inner
   * strip (`#ttp-ecom-strip`), not the wrapper: the wrapper is fixed at `bottom:0`
   * with bottom padding (incl. safe-area inset), so its bottom edge hugs the
   * viewport bottom and would wrongly forbid any downward drag. The drag offset is
   * applied to the wrapper, but both elements move together by the same amount, so
   * the inner strip's offset-0 rect is just its current rect minus the offset.
   */
  _dragBaseRect() {
    const o = this._dragOffset || { x: 0, y: 0 };
    const inner = (this._el && this._el.querySelector('#ttp-ecom-strip')) || this._el;
    const r = inner.getBoundingClientRect();
    return { left: r.left - o.x, top: r.top - o.y, right: r.right - o.x, bottom: r.bottom - o.y };
  }

  /**
   * Clamp a drag offset so the strip stays within the viewport. An 8px margin is
   * kept on the top/left/right edges. The bottom is allowed to drag *past* the
   * viewport edge so the strip can be tucked partly off-screen — but at least
   * `MIN_VISIBLE` (10px) of its top stays on screen so it can always be grabbed
   * and dragged back up.
   */
  _clampDragOffset(x, y, base) {
    if (!base) return { x, y };
    const margin = 8;
    const MIN_VISIBLE = 10;
    let minX = margin - base.left, maxX = window.innerWidth - margin - base.right;
    let minY = margin - base.top, maxY = window.innerHeight - MIN_VISIBLE - base.top;
    if (minX > maxX) maxX = minX;
    if (minY > maxY) maxY = minY;
    return { x: Math.min(Math.max(x, minX), maxX), y: Math.min(Math.max(y, minY), maxY) };
  }

  /** Apply the drag offset on top of the centering transform. */
  _applyDragOffset() {
    if (!this._el) return;
    const o = this._dragOffset || { x: 0, y: 0 };
    this._el.style.setProperty('transform', `translateX(-50%) translate(${o.x}px, ${o.y}px)`, 'important');
  }

  /** Re-clamp the dragged strip to the current viewport (on show / resize). */
  reclampDrag() {
    if (!this._dragEnabled || !this._el) return;
    const r = this._el.getBoundingClientRect();
    if (!r.width && !r.height) return; // hidden — nothing to clamp yet
    this._dragOffset = this._clampDragOffset(this._dragOffset.x, this._dragOffset.y, this._dragBaseRect());
    this._applyDragOffset();
  }

  destroy() {
    if (this._recipeUnsub) {
      this._recipeUnsub();
      this._recipeUnsub = null;
    }
    if (this._recipeOutsideClick) {
      document.removeEventListener('click', this._recipeOutsideClick);
      this._recipeOutsideClick = null;
    }
    if (this._pulseTimer) {
      clearTimeout(this._pulseTimer);
      this._pulseTimer = null;
    }
    if (this._el && this._el.parentNode) this._el.parentNode.removeChild(this._el);
    this._el = null;
    const style = document.getElementById('ttp-ecom-strip-styles');
    if (style) style.parentNode.removeChild(style);
  }

  _reset() {
    this._kbdOpen = false;
    const $ = (id) => this._el.querySelector('#' + id);
    const tx = $('ttp-ecom-transcript');
    if (tx) {
      tx.textContent = 'Connecting…';
      tx.classList.remove('ttp-ecom-transcript--user');
      tx.style.color = '';
      const stripCfg = { language: this._language, direction: this._direction };
      tx.setAttribute('dir', dirForTranscriptSnippet(tx.textContent, stripCfg));
      if (configPrefersRtlText(stripCfg) && this._language?.trim()) tx.setAttribute('lang', this._language.trim());
      else tx.removeAttribute('lang');
    }
    const live = $('ttp-ecom-live-label');
    if (live) {
      live.textContent = 'LIVE · 00:00';
      live.style.color = '';
    }
    const dot = $('ttp-ecom-dot');
    if (dot) {
      dot.style.background = '';
      dot.classList.remove('paused');
    }
    $('ttp-ecom-input-row')?.classList.remove('open');
    $('ttp-ecom-btn-kbd')?.classList.remove('ttp-ecom-ctrl-active');
    this._syncMiniWave(false);
    this.syncFromVoice();
  }

  _injectStyles() {
    if (document.getElementById('ttp-ecom-strip-styles')) return;
    const staleClip = document.getElementById('ttp-ecom-tab-clip-defs');
    if (staleClip) staleClip.remove();
    const style = document.createElement('style');
    style.id = 'ttp-ecom-strip-styles';
    style.textContent = EcommerceVoiceStrip._CSS;
    document.head.appendChild(style);
  }
}

EcommerceVoiceStrip._CSS = `
#ttp-ecom-strip-wrapper {
  position: fixed;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: min(720px, calc(100vw - 40px));
  max-width: calc(100vw - 40px);
  z-index: 2147483647;
  box-sizing: border-box;
  padding: 0 14px max(14px, env(safe-area-inset-bottom, 0px));
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  font-feature-settings: "kern" 1, "liga" 1;
  pointer-events: none;
}
#ttp-ecom-strip-wrapper * { box-sizing: border-box; }

/* Layered glass dock: rim light, soft lift, optional backdrop tint */
#ttp-ecom-strip {
  pointer-events: auto;
  position: relative;
  isolation: isolate;
  margin: 0;
  border-radius: 26px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: var(--ecom-strip-bg, linear-gradient(145deg, #5b21b6 0%, #3730a3 42%, #1e1b4b 100%));
  background-clip: padding-box;
  color: var(--ecom-fg, #ffffff);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.18) inset,
    0 1px 0 rgba(255, 255, 255, 0.14) inset,
    0 -20px 40px -8px rgba(0, 0, 0, 0.22) inset,
    0 12px 32px rgba(0, 0, 0, 0.22),
    0 28px 56px -16px rgba(0, 0, 0, 0.45),
    0 0 80px -20px rgba(99, 102, 241, 0.35);
  overflow: visible;
}

#ttp-ecom-strip::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  z-index: 0;
  background: linear-gradient(
    165deg,
    rgba(255, 255, 255, 0.08) 0%,
    rgba(255, 255, 255, 0) 42%,
    rgba(0, 0, 0, 0.12) 100%
  );
}

/* LIVE chip — glass capsule (backdrop blur), straddles top edge */
#ttp-ecom-live-edge {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  z-index: 4;
  max-width: calc(100% - 52px);
  pointer-events: none;
  padding: 3px 14px 4px;
  border-radius: 999px;
  background: rgba(8, 6, 24, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.28);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.35),
    0 4px 16px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}
@supports (backdrop-filter: blur(1px)) {
  #ttp-ecom-live-edge {
    backdrop-filter: blur(14px) saturate(180%);
    -webkit-backdrop-filter: blur(14px) saturate(180%);
  }
}

#ttp-ecom-main-row {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 16px 12px;
  min-height: 48px;
}

#ttp-ecom-footer {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px 10px;
  margin-top: -4px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 6px;
}
.ttp-ecom-info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  line-height: 0;
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.35);
  transition: color 0.2s, background 0.2s;
}
.ttp-ecom-info-btn:hover {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.08);
}
.ttp-ecom-info-btn svg { display: block; }
#ttp-ecom-footer span {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.35);
  white-space: nowrap;
}
#ttp-ecom-footer a {
  color: rgba(167, 139, 250, 0.95);
  text-decoration: none;
  font-weight: 600;
}
#ttp-ecom-footer a:hover { opacity: 0.85; }
#ttp-ecom-footer a b { font-weight: 700; }

.ttp-ecom-recipe-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.ttp-ecom-recipe-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 7px 3px 5px;
  margin: 0;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(167, 139, 250, 0.42);
  border-radius: 999px;
  cursor: pointer;
  line-height: 1;
  color: rgba(255, 255, 255, 0.92);
  transition: color 0.2s, background 0.2s, border-color 0.2s, box-shadow 0.2s;
}
.ttp-ecom-recipe-btn:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(196, 181, 253, 0.65);
}
.ttp-ecom-recipe-btn svg { display: block; flex-shrink: 0; }
.ttp-ecom-recipe-count {
  font-size: 10px;
  font-weight: 800;
  min-width: 12px;
  text-align: center;
  letter-spacing: 0.02em;
}
.ttp-ecom-recipe-btn--celebrate {
  animation: ttp-ecom-recipe-celebrate 1.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}
.ttp-ecom-recipe-btn--celebrate::after {
  content: "";
  position: absolute;
  inset: -3px;
  border-radius: 999px;
  border: 2px solid rgba(74, 222, 128, 0.75);
  animation: ttp-ecom-recipe-ring 1.35s ease-out forwards;
  pointer-events: none;
}
.ttp-ecom-recipe-count--pop {
  animation: ttp-ecom-recipe-count-pop 1.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}
@keyframes ttp-ecom-recipe-celebrate {
  0% {
    transform: scale(1);
    color: rgba(167, 139, 250, 0.92);
    background: rgba(255, 255, 255, 0.06);
    box-shadow: none;
  }
  18% {
    transform: scale(1.22);
    color: #86efac;
    background: rgba(74, 222, 128, 0.24);
    border-color: rgba(74, 222, 128, 0.55);
    box-shadow: 0 0 22px rgba(74, 222, 128, 0.5), 0 0 40px rgba(74, 222, 128, 0.2);
  }
  36% {
    transform: scale(1.06);
    color: #bbf7d0;
    box-shadow: 0 0 14px rgba(74, 222, 128, 0.35);
  }
  54% {
    transform: scale(1.16);
    color: #4ade80;
    background: rgba(74, 222, 128, 0.3);
    box-shadow: 0 0 28px rgba(74, 222, 128, 0.55);
  }
  72% {
    transform: scale(1.08);
    color: #86efac;
  }
  100% {
    transform: scale(1);
    color: rgba(167, 139, 250, 0.95);
    background: rgba(74, 222, 128, 0.1);
    border-color: rgba(74, 222, 128, 0.28);
    box-shadow: 0 0 12px rgba(74, 222, 128, 0.15);
  }
}
@keyframes ttp-ecom-recipe-ring {
  0% { transform: scale(0.9); opacity: 0.85; }
  100% { transform: scale(2.1); opacity: 0; }
}
@keyframes ttp-ecom-recipe-count-pop {
  0%, 100% { transform: scale(1); }
  20% { transform: scale(1.45); color: #fff; }
  45% { transform: scale(1.15); }
  60% { transform: scale(1.35); color: #dcfce7; }
}
.ttp-ecom-recipe-popover {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 180px;
  max-width: 240px;
  padding: 8px 0 6px;
  border-radius: 14px;
  background: rgba(12, 10, 28, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(0, 0, 0, 0.2);
  z-index: 6;
  pointer-events: auto;
}
@supports (backdrop-filter: blur(1px)) {
  .ttp-ecom-recipe-popover {
    backdrop-filter: blur(16px) saturate(160%);
    -webkit-backdrop-filter: blur(16px) saturate(160%);
  }
}
.ttp-ecom-recipe-popover-label {
  display: block;
  padding: 2px 12px 6px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
}
.ttp-ecom-recipe-row {
  display: block;
  width: 100%;
  padding: 8px 12px;
  margin: 0;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.92);
  font: 500 12px/1.35 ui-sans-serif, system-ui, sans-serif;
  text-align: start;
  cursor: pointer;
  transition: background 0.15s;
}
.ttp-ecom-recipe-row:hover {
  background: rgba(255, 255, 255, 0.08);
}
.ttp-ecom-recipe-row-title {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ttp-ecom-recipe-more {
  padding: 4px 12px 2px;
  font-size: 10px;
  color: rgba(167, 139, 250, 0.85);
  text-align: center;
}

#ttp-ecom-left-controls,
#ttp-ecom-right-cluster {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
}

/* Inset “meter bridge” for transcript — keeps one-line layout, adds depth */
#ttp-ecom-center {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  text-align: start;
  padding: 7px 12px 7px 14px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.07),
    inset 0 -1px 0 rgba(0, 0, 0, 0.12);
}

#ttp-ecom-live-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
}

#ttp-ecom-live-label {
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ecom-accent, #4ade80);
  opacity: 0.98;
  white-space: nowrap;
}

#ttp-ecom-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ecom-accent, #4ade80);
  flex-shrink: 0;
  box-shadow: 0 0 14px rgba(74, 222, 128, 0.45);
  animation: ttp-ecom-blink 1.35s ease-in-out infinite;
}
#ttp-ecom-dot.paused {
  animation: none;
  opacity: 0.45;
  background: rgba(255,255,255,0.35);
  box-shadow: none;
}
@keyframes ttp-ecom-blink {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.25; transform: scale(0.92); }
}

#ttp-ecom-transcript {
  font-size: 12px;
  line-height: 1.42;
  letter-spacing: 0.015em;
  color: rgba(255, 255, 255, 0.94);
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  word-break: break-word;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

#ttp-ecom-transcript.ttp-ecom-transcript--user {
  color: rgba(255, 244, 214, 0.98);
  font-weight: 600;
  letter-spacing: 0.02em;
  text-shadow: 0 0 28px rgba(251, 191, 36, 0.22);
}

/* Squircle glass controls */
#ttp-ecom-strip .ttp-ecom-ctrl {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: linear-gradient(165deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.05) 100%);
  color: rgba(255, 255, 255, 0.92);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  position: relative;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.18),
    0 2px 8px rgba(0, 0, 0, 0.15);
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.14s ease;
  outline: none;
}
#ttp-ecom-strip .ttp-ecom-ctrl:hover {
  background: linear-gradient(165deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 100%);
  border-color: rgba(255, 255, 255, 0.32);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.22),
    0 4px 14px rgba(0, 0, 0, 0.2);
}
#ttp-ecom-strip .ttp-ecom-ctrl:active { transform: scale(0.94); }
#ttp-ecom-strip .ttp-ecom-ctrl:focus-visible {
  outline: 2px solid rgba(165, 180, 252, 0.95);
  outline-offset: 2px;
}
#ttp-ecom-strip .ttp-ecom-ctrl-active {
  background: linear-gradient(165deg, rgba(129, 140, 248, 0.45) 0%, rgba(99, 102, 241, 0.28) 100%) !important;
  border-color: rgba(196, 181, 253, 0.65) !important;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 0 0 1px rgba(99, 102, 241, 0.35),
    0 6px 18px rgba(79, 70, 229, 0.35) !important;
}
#ttp-ecom-strip .ttp-ecom-ctrl-warn {
  background: linear-gradient(165deg, rgba(252, 165, 165, 0.22) 0%, rgba(239, 68, 68, 0.2) 100%) !important;
  border-color: rgba(252, 165, 165, 0.55) !important;
}

#ttp-ecom-strip .ttp-ecom-ctrl-end {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  background: linear-gradient(165deg, #fb7185 0%, var(--ecom-end, #ef4444) 52%, #991b1b 100%) !important;
  border: none !important;
  color: #fff !important;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.25),
    0 0 0 2px rgba(0, 0, 0, 0.12),
    0 6px 22px rgba(239, 68, 68, 0.48);
  transform: rotate(135deg);
  transition: filter 0.18s ease, transform 0.14s ease, box-shadow 0.2s ease;
}
#ttp-ecom-strip .ttp-ecom-ctrl-end:hover {
  filter: brightness(1.06);
  transform: rotate(135deg) scale(1.05);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.28),
    0 0 0 2px rgba(0, 0, 0, 0.12),
    0 10px 28px rgba(239, 68, 68, 0.52);
}
#ttp-ecom-strip .ttp-ecom-ctrl-end:active {
  transform: rotate(135deg) scale(0.94);
}

/* Mini waveform */
#ttp-ecom-strip .ttp-ecom-mini-wave {
  display: flex;
  align-items: center;
  gap: 3px;
  height: 24px;
  padding: 0 4px;
  opacity: 0.9;
}
#ttp-ecom-strip .ttp-ecom-mini-wave .ttp-ecom-wb {
  width: 3px;
  border-radius: 3px;
  background: linear-gradient(180deg, #ddd6fe 0%, var(--ecom-accent, #a78bfa) 100%);
  animation: ttp-ecom-wa 0.78s ease-in-out infinite;
}
#ttp-ecom-strip .ttp-ecom-mini-wave .ttp-ecom-wb:nth-child(1) { height: 6px; animation-delay: 0s; }
#ttp-ecom-strip .ttp-ecom-mini-wave .ttp-ecom-wb:nth-child(2) { height: 11px; animation-delay: 0.1s; }
#ttp-ecom-strip .ttp-ecom-mini-wave .ttp-ecom-wb:nth-child(3) { height: 16px; animation-delay: 0.2s; }
#ttp-ecom-strip .ttp-ecom-mini-wave .ttp-ecom-wb:nth-child(4) { height: 9px; animation-delay: 0.15s; }
#ttp-ecom-strip .ttp-ecom-mini-wave .ttp-ecom-wb:nth-child(5) { height: 12px; animation-delay: 0.05s; }
@keyframes ttp-ecom-wa {
  0%, 100% { transform: scaleY(0.38); opacity: 0.5; }
  50% { transform: scaleY(1); opacity: 1; }
}
#ttp-ecom-strip .ttp-ecom-mini-wave .ttp-ecom-wb.paused {
  animation-play-state: paused;
  transform: scaleY(0.32);
  opacity: 0.28;
}

/* Input row */
#ttp-ecom-input-row {
  display: none;
  position: relative;
  z-index: 1;
  align-items: center;
  gap: 10px;
  padding: 0 14px 12px;
  margin-top: -2px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
#ttp-ecom-input-row.open {
  display: flex;
}
#ttp-ecom-input {
  flex: 1;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(0, 0, 0, 0.22);
  color: #fff;
  font-size: 14px;
  padding: 0 14px;
  outline: none;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
#ttp-ecom-input::placeholder { color: rgba(255, 255, 255, 0.42); }
#ttp-ecom-input:focus {
  border-color: rgba(165, 180, 252, 0.65);
  box-shadow:
    inset 0 1px 2px rgba(0, 0, 0, 0.2),
    0 0 0 3px rgba(99, 102, 241, 0.25);
}

#ttp-ecom-strip .ttp-ecom-send {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(165deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.1) 100%);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transition: background 0.18s ease, transform 0.12s ease;
}
#ttp-ecom-strip .ttp-ecom-send:hover {
  background: linear-gradient(165deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.14) 100%);
}
#ttp-ecom-strip .ttp-ecom-send:active { transform: scale(0.94); }

@media (max-width: 520px) {
  #ttp-ecom-strip .ttp-ecom-mini-wave { display: none; }
  #ttp-ecom-main-row { gap: 7px; padding: 16px 12px 10px; }
  #ttp-ecom-strip .ttp-ecom-ctrl { width: 36px; height: 36px; }
  #ttp-ecom-strip .ttp-ecom-ctrl-end { width: 40px; height: 40px; }
}

@media (prefers-reduced-motion: reduce) {
  #ttp-ecom-dot { animation: none; opacity: 0.9; }
  #ttp-ecom-strip .ttp-ecom-mini-wave .ttp-ecom-wb { animation: none; opacity: 0.65; }
  .ttp-ecom-recipe-btn--celebrate,
  .ttp-ecom-recipe-count--pop {
    animation: none !important;
  }
  .ttp-ecom-recipe-btn--celebrate::after { display: none; }
}
`;
